import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Eye,
  Download,
  Search,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  Archive
} from 'lucide-react';
import api from '../../services/apiService';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Clock },
  pending_review: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  pending_approval: { label: 'Pending Approval', color: 'bg-blue-100 text-blue-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-500', icon: Archive },
};

const RiskAssessmentList = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAssessment, setNewAssessment] = useState({
    template: '',
    title: '',
    description: '',
    location: '',
    work_activity: '',
    project: '',
    issued_by: '',
    client: '',
    prepared_by: '',
    authorised_by: '',
    issue_no: '',
    doc_control_title: '',
    doc_control_project: ''
  });
  const [actionLoading, setActionLoading] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      const [assessmentsRes, templatesRes] = await Promise.all([
        api.get('/risk-assessments/', {
          params: statusFilter ? { status: statusFilter } : {}
        }),
        api.get('/risk-assessment-templates/')
      ]);
      setAssessments(assessmentsRes.data);
      setTemplates(templatesRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        ...newAssessment,
        template: newAssessment.template ? parseInt(newAssessment.template) : null
      };
      const response = await api.post('/risk-assessments/', payload);
      setAssessments([response.data, ...assessments]);
      setShowCreateModal(false);
      setNewAssessment({
        template: '',
        title: '',
        description: '',
        location: '',
        work_activity: '',
        project: '',
        issued_by: '',
        client: '',
        prepared_by: '',
        authorised_by: '',
        issue_no: '',
        doc_control_title: '',
        doc_control_project: ''
      });
    } catch (err) {
      console.error('Failed to create assessment:', err);
    }
  };

  const handleAction = async (id, action, data = {}) => {
    setActionLoading(id);
    try {
      let response;
      switch (action) {
        case 'submit_for_review':
          response = await api.post(`/risk-assessments/${id}/submit_for_review/`, data);
          break;
        case 'approve_review':
          response = await api.post(`/risk-assessments/${id}/review/`, { action: 'approve', ...data });
          break;
        case 'reject_review':
          response = await api.post(`/risk-assessments/${id}/review/`, { action: 'reject', ...data });
          break;
        case 'approve':
          response = await api.post(`/risk-assessments/${id}/approve/`, data);
          break;
        case 'reject':
          response = await api.post(`/risk-assessments/${id}/reject/`, data);
          break;
        case 'archive':
          response = await api.post(`/risk-assessments/${id}/archive/`, data);
          break;
        default:
          return;
      }

      // Update the assessment in the list
      setAssessments(assessments.map(a =>
        a.id === id ? response.data : a
      ));
    } catch (err) {
      console.error(`Failed to ${action}:`, err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownload = async (id, assessmentNumber) => {
    try {
      const response = await api.get(`/risk-assessments/${id}/download/`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `risk_assessment_${assessmentNumber || id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download:', err);
    }
  };

  const handlePreview = async (id) => {
    setPreviewLoading(true);
    setShowPreviewModal(true);
    try {
      const response = await api.get(`/risk-assessments/${id}/download/`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setPreviewPdfUrl(url);
    } catch (err) {
      console.error('Failed to load preview:', err);
      setShowPreviewModal(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setShowPreviewModal(false);
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
  };

  const filteredAssessments = assessments.filter(assessment =>
    assessment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assessment.assessment_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assessment.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Risk Assessments
          </h1>
          <p className="text-gray-600 mt-1">Manage your risk assessment documents</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          New Assessment
        </button>
      </div>

      <div className="flex gap-4 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search assessments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg"
        >
          <option value="">All Status</option>
          {Object.entries(STATUS_CONFIG).map(([value, config]) => (
            <option key={value} value={value}>{config.label}</option>
          ))}
        </select>
      </div>

      {filteredAssessments.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <Shield className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No assessments found</h3>
          <p className="text-gray-500 mt-1">
            {searchTerm || statusFilter ? 'Try adjusting your filters' : 'Create your first risk assessment'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Assessment</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Location</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Assessor</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredAssessments.map(assessment => {
                const statusConfig = STATUS_CONFIG[assessment.status] || STATUS_CONFIG.draft;
                const StatusIcon = statusConfig.icon;

                return (
                  <tr key={assessment.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{assessment.title}</div>
                      <div className="text-sm text-gray-500">
                        {assessment.assessment_number || `#${assessment.id}`}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {assessment.location || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${statusConfig.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {assessment.assessor_name || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {assessment.assessment_date
                        ? new Date(assessment.assessment_date).toLocaleDateString()
                        : '-'
                      }
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* Workflow actions based on status */}
                        {(['draft', 'pending_review', 'pending_approval'].includes(assessment.status) || assessment.can_approve) && (
                          <>
                            <button
                              onClick={() => handleAction(assessment.id, 'approve')}
                              disabled={actionLoading === assessment.id}
                              className="p-1.5 hover:bg-green-100 rounded text-green-600"
                              title="Approve"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleAction(assessment.id, 'reject')}
                              disabled={actionLoading === assessment.id}
                              className="p-1.5 hover:bg-red-100 rounded text-red-600"
                              title="Reject"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}

                        <button
                          onClick={() => handlePreview(assessment.id)}
                          className="p-1.5 hover:bg-gray-100 rounded"
                          title="Preview PDF"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownload(assessment.id, assessment.assessment_number)}
                          className="p-1.5 hover:bg-gray-100 rounded"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Assessment Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-5xl w-full mx-4">
            <h3 className="text-lg font-medium mb-4">Create New Assessment</h3>

            <div className="grid grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Template *</label>
                  <select
                    value={newAssessment.template}
                    onChange={(e) => setNewAssessment({ ...newAssessment, template: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="">Select a template</option>
                    {templates.filter(t => t.is_active).map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Title *</label>
                  <input
                    type="text"
                    value={newAssessment.title}
                    onChange={(e) => setNewAssessment({ ...newAssessment, title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Assessment title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Location</label>
                  <input
                    type="text"
                    value={newAssessment.location}
                    onChange={(e) => setNewAssessment({ ...newAssessment, location: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Work location"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Work Activity</label>
                  <input
                    type="text"
                    value={newAssessment.work_activity}
                    onChange={(e) => setNewAssessment({ ...newAssessment, work_activity: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Description of work activity"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={newAssessment.description}
                    onChange={(e) => setNewAssessment({ ...newAssessment, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={3}
                    placeholder="Additional description"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h4 className="text-sm font-medium mb-3">Header Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Project</label>
                      <input
                        type="text"
                        value={newAssessment.project}
                        onChange={(e) => setNewAssessment({ ...newAssessment, project: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="e.g. KLEUTERBACH Wind Farm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Issued by</label>
                      <input
                        type="text"
                        value={newAssessment.issued_by}
                        onChange={(e) => setNewAssessment({ ...newAssessment, issued_by: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="e.g. Omega Wind Sp. z o. o."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Client</label>
                      <input
                        type="text"
                        value={newAssessment.client}
                        onChange={(e) => setNewAssessment({ ...newAssessment, client: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="e.g. Vestas NCE"
                      />
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="text-sm font-medium mb-3">Document Control</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Title</label>
                      <input
                        type="text"
                        value={newAssessment.doc_control_title}
                        onChange={(e) => setNewAssessment({ ...newAssessment, doc_control_title: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="e.g. Document Control"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Project</label>
                      <input
                        type="text"
                        value={newAssessment.doc_control_project}
                        onChange={(e) => setNewAssessment({ ...newAssessment, doc_control_project: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="Project name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Prepared by</label>
                      <input
                        type="text"
                        value={newAssessment.prepared_by}
                        onChange={(e) => setNewAssessment({ ...newAssessment, prepared_by: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Authorised by</label>
                      <input
                        type="text"
                        value={newAssessment.authorised_by}
                        onChange={(e) => setNewAssessment({ ...newAssessment, authorised_by: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Issue No.</label>
                      <input
                        type="text"
                        value={newAssessment.issue_no}
                        onChange={(e) => setNewAssessment({ ...newAssessment, issue_no: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="e.g. Rev.2.16"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newAssessment.template || !newAssessment.title}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] mx-4 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium">PDF Preview</h3>
              <button
                onClick={closePreview}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-hidden">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : previewPdfUrl ? (
                <iframe
                  src={previewPdfUrl}
                  className="w-full h-full border rounded"
                  title="PDF Preview"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Failed to load preview
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskAssessmentList;
